// Lets people in the place work on a shared board, shown on the stage: each person puts notes,
// images, cards for shared files and links on it, writes in notes, moves and removes items,
// everyone sees the changes, and a person's own change shows at once. It also keeps who shared each
// file and which files were removed.
//
// The board, as each person holds it: items by their ID, each whole as last changed with when it
// changed, and `file:{id}` for who shared a file, or that it was removed. Each person publishes the
// board they hold as the state `board`, again whenever it changes, and merges everyone else's into
// it (merge.ts: per key the latest change), so every page ends with the same board, changes of
// different items all survive, and the board stays while anyone is there. A deletion stays as
// `{ deleted, at }`, so nothing brings it back. An image or a file on the board is a shared file
// (files.ts); a link is an http(s) URL.

import { State, type Connection } from 'tablebox.io';
import { h, icon, iconButton, lang, nameDetail, t } from './dom.ts';
import { isImage, type FileRecord, type Meta, type SharedFiles } from './files.ts';
import { newId } from './link.ts';
import { merge } from './merge.ts';
import { formatSize } from './text.ts';

/** A card's width, 14rem (collab.css), in pixels: the room a new or dropped item is kept inside the board. */
const CARD = 14 * parseFloat(getComputedStyle(document.documentElement).fontSize);

type Kind = 'note' | 'image' | 'file' | 'link';
const KINDS = new Set<unknown>(['note', 'image', 'file', 'link']);
/** An item as a person last had it, or its deletion; `at` is when it changed, in ms since the epoch. */
interface Entry {
  kind?: Kind;
  text?: string;
  file?: string;
  name?: string;
  size?: number;
  w?: number;
  url?: string;
  x?: number;
  y?: number;
  deleted?: true;
  at: number;
}
/** The board: items by ID, and `file:{id}` for each shared file. */
type Board = Record<string, Entry | FileRecord>;

/** An item on the page: for a note its textarea; for an image or a file its body and what says the file is gone. */
interface Shown {
  element: HTMLElement;
  text?: HTMLTextAreaElement;
  body?: HTMLElement;
  gone?: HTMLElement;
  dragging: boolean;
}

const fraction = (value: unknown) => (typeof value === 'number' && value >= 0 && value <= 1 ? value : 0);
const isObject = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null;
const FILE = 'file:';
/** `text` as an http or https URL, or undefined. */
const httpUrl = (text: unknown) => {
  try {
    const url = new URL(String(text ?? '').trim());
    return url.protocol === 'http:' || url.protocol === 'https:' ? url : undefined;
  } catch {
    return undefined;
  }
};

/**
 * The board view (`#board-view`: the board with its tools floating over its corner); `showBoard` opens the
 * view when a file is put on the board from the Files panel (`place`); `shareFile` and `removeFile` note who
 * shared a file and that it was removed, and `refresh` shows again whether each item's file is shared.
 */
export function boardSection(connection: Connection, files: SharedFiles, showBoard: () => void): {
  view: HTMLElement; place: (id: string, meta: Meta) => void; shareFile: (id: string, meta: Meta) => void; removeFile: (id: string) => void; refresh: () => void; close: () => void;
} {
  const me = connection.id;
  /** The board this person holds: its own changes and everyone else's merged, per key the latest. */
  const board: Board = {};
  const mine = State.publish<Board>(connection, 'board', board);
  let last = 0;
  const now = () => (last = Math.max(Date.now(), last + 1));
  const shown = new Map<string, Shown>();
  const area = h('div', { id: 'board' });
  const items = () => Object.entries(board).filter((entry): entry is [string, Entry] => !entry[0].startsWith(FILE) && isObject(entry[1]));
  let closed = false;

  /**
   * Changes `key` to `value` on this person's board and publishes it; the board shows it at once.
   * Once the board is closed nothing is written, as a share that finishes after the page closed would otherwise throw.
   */
  const write = (key: string, value: Entry | FileRecord) => {
    if (closed) return;
    board[key] = value;
    mine.set(board);
    render();
  };
  /** Makes an item (a change with a `kind`) or changes one; a deletion is `{ deleted, at }` alone. */
  const change = (id: string, entry: Omit<Entry, 'at'>) => {
    const current = board[id] as Entry | undefined;
    const kept = current?.deleted ? undefined : current;
    // A drag's end or a keystroke that lands after someone deleted the item does not bring it back.
    if (!kept && !entry.kind) return;
    write(id, entry.deleted ? { deleted: true, at: now() } : { ...kept, ...entry, at: now() });
  };
  /**
   * `x` and `y`, as fractions, with an item `width` by `height` pixels kept inside the board, its bar
   * clear of the tools: left of the tools' right edge, an item starts under them.
   */
  const inside = (x: number, y: number, width: number, height: number) => {
    const box = area.getBoundingClientRect();
    const clamp = (value: number, room: number) => Math.max(0, Math.min(Math.max(0, room), value));
    x = clamp(x, 1 - width / box.width);
    y = clamp(y, 1 - height / box.height);
    if (x * box.width < tools.offsetLeft + tools.offsetWidth) y = Math.max(y, (tools.offsetTop + tools.offsetHeight + 8) / box.height);
    return { x, y };
  };
  /** Where `event` is on the board, as fractions, for an item `width` by `height` pixels. */
  const at = (event: { clientX: number; clientY: number }, width: number, height: number) => {
    const box = area.getBoundingClientRect();
    return inside((event.clientX - box.left) / box.width, (event.clientY - box.top) / box.height, width, height);
  };
  /** A free spot for a new item of a card's size, among a few tries. */
  const spot = () => {
    const taken = items().map(([, entry]) => entry).filter(entry => !entry.deleted);
    let where = { x: 0, y: 0 };
    for (let i = 0; i < 20; i++) {
      where = inside(0.05 + Math.random() * 0.6, 0.12 + Math.random() * 0.55, CARD, CARD);
      if (!taken.some(entry => Math.abs(fraction(entry.x) - where.x) < 0.12 && Math.abs(fraction(entry.y) - where.y) < 0.15)) break;
    }
    return where;
  };
  /** Puts the shared file `id` on the board at `where`: a picture for an image, a card otherwise. */
  const place = (id: string, meta: Meta, where: { x: number; y: number }) => {
    if (isImage(meta.type)) change(newId(), { kind: 'image', file: id, name: meta.name, w: 0.25, ...where });
    else change(newId(), { kind: 'file', file: id, name: meta.name, size: meta.size, ...where });
  };
  /** Shares `file` and puts it on the board. */
  const share = async (file: File, where: { x: number; y: number }) => {
    const made = await files.share(file);
    if (made) place(made.id, made.meta, where);
  };
  const addLink = (url: URL, where: { x: number; y: number }) => change(newId(), { kind: 'link', url: url.href, ...where });

  const position = (element: HTMLElement, x: number, y: number) => {
    element.style.left = `${x * 100}%`;
    element.style.top = `${y * 100}%`;
    element.style.maxWidth = `${(1 - x) * 100}%`;  // near the right edge an item shrinks rather than overflows
    element.dataset.x = x.toFixed(3);
    element.dataset.y = y.toFixed(3);
  };
  const text = (value: unknown) => (typeof value === 'string' ? value : '');
  const add = (id: string, value: Entry) => {
    const kind = value.kind as Kind;
    const bar = h('div', { class: 'item-bar', title: () => t('moveItem') },
      h('button', { type: 'button', class: 'delete', 'aria-label': () => t('deleteItem'), onclick: () => change(id, { deleted: true }) }, '×'));
    const element = h('div', { class: kind === 'note' ? 'item note' : 'item', 'data-id': id, 'data-kind': kind }, bar);
    const entry: Shown = { element, dragging: false };
    if (kind === 'note') {
      const field: HTMLTextAreaElement = h('textarea', { 'aria-label': () => t('note'), rows: '3', oninput: () => change(id, { text: field.value }), onblur: () => render(),
        onkeydown: (event: KeyboardEvent) => {
          // Escape leaves the note; the board view stays.
          if (event.key !== 'Escape') return;
          event.stopPropagation();
          field.blur();
        } });
      entry.text = field;
      element.append(field);
    } else if (kind === 'link') {
      const url = httpUrl(value.url);
      element.append(h('a', { class: 'item-card', href: url?.href, target: '_blank', rel: 'noopener', draggable: 'false' },
        icon('link'), nameDetail(url?.host ?? '', () => (url ? url.pathname + url.search + url.hash : ''))));
    } else {
      const file = text(value.file);
      const name = text(value.name);
      if (kind === 'image') {
        const w = typeof value.w === 'number' && value.w > 0 && value.w <= 1 ? value.w : 0.25;
        element.style.width = `${w * 100}%`;
        entry.body = h('img', { alt: name, draggable: 'false' });  // its picture is asked for by render(), while the file is listed
      } else {
        const size = typeof value.size === 'number' ? value.size : 0;
        entry.body = h('button', { type: 'button', class: 'item-card', onclick: () => files.open(file) }, icon('file'), nameDetail(name, () => formatSize(size, lang)));
      }
      // In the body's place while the file is not shared, with the kind's icon, so only the second line changes.
      entry.gone = h('p', { class: 'item-card', hidden: true }, icon(kind), nameDetail(name, () => t('fileGone')));
      element.append(entry.body, entry.gone);
    }
    // Dragging the bar moves the item; the window gets the moves and the release, so a drag that
    // leaves the bar goes on, and the change goes out when it is dropped, if it moved.
    bar.addEventListener('pointerdown', event => {
      if (event.target !== bar || event.button !== 0 || !event.isPrimary) return;
      // To the front, so the dragged item is never under a later one.
      area.append(element);
      const pointer = event.pointerId;
      // Where on the item it was taken, so it moves with the pointer instead of jumping to it.
      const [dx, dy] = [event.clientX - element.getBoundingClientRect().left, event.clientY - element.getBoundingClientRect().top];
      let moved: { x: number; y: number } | undefined;
      entry.dragging = true;
      try {
        bar.setPointerCapture(pointer);
      } catch {
        // The window listeners follow the pointer anyway.
      }
      const move = (event: PointerEvent) => {
        if (event.pointerId !== pointer) return;
        moved = at({ clientX: event.clientX - dx, clientY: event.clientY - dy }, element.offsetWidth, element.offsetHeight);
        position(element, moved.x, moved.y);
      };
      const end = (event: PointerEvent) => {
        if (event.pointerId !== pointer) return;
        removeEventListener('pointermove', move, true);
        removeEventListener('pointerup', end, true);
        removeEventListener('pointercancel', end, true);
        entry.dragging = false;
        if (moved) change(id, moved);
      };
      addEventListener('pointermove', move, { capture: true });
      addEventListener('pointerup', end, { capture: true });
      addEventListener('pointercancel', end, { capture: true });
    });
    shown.set(id, entry);
    area.append(element);
    return entry;
  };

  /** Shows the items that are not deleted, each saying when its file is no longer shared; tells the files who shared each and which were removed. */
  function render() {
    files.records(new Map(Object.entries(board).filter(([key, value]) => key.startsWith(FILE) && isObject(value)).map(([key, value]) => [key.slice(FILE.length), value as FileRecord])));
    const live = items().filter(([, entry]) => !entry.deleted && KINDS.has(entry.kind));
    for (const [id, value] of live) {
      const entry = shown.get(id) ?? add(id, value);
      if (!entry.dragging) position(entry.element, fraction(value.x), fraction(value.y));
      if (entry.text && document.activeElement !== entry.text && entry.text.value !== text(value.text)) entry.text.value = text(value.text);
      if (entry.body && entry.gone) {
        const file = text(value.file);
        const missing = !files.has(file);
        entry.body.hidden = missing;
        entry.gone.hidden = !missing;
        // The picture is asked for while its file is listed, until it shows: a read that ended or failed is read again.
        const img = entry.body;
        if (!missing && img instanceof HTMLImageElement && !img.src) void files.url(file).then(url => (img.src = url), () => {});
      }
    }
    const ids = new Set(live.map(([id]) => id));
    for (const [id, { element }] of shown) {
      if (ids.has(id)) continue;
      element.remove();
      shown.delete(id);
    }
  }

  // Everyone else's board, merged into this person's, which goes out again when it changed.
  const others = State.subscribe<Board>(connection, 'board', (id, value) => {
    if (closed || id === me || !merge(board, value)) return;
    mine.set(board);
    render();
  });
  render();

  // The tools, floating over the board's corner: a note, an image from a file, and a link typed in a form.
  const imageInput = h('input', { type: 'file', accept: 'image/*', multiple: true, id: 'image-input', class: 'visually-hidden', tabindex: '-1', onchange: () => {
    for (const file of imageInput.files ?? []) void share(file, spot());
    imageInput.value = '';
  } });
  const addLinkButton = iconButton({ id: 'add-link', onclick: () => {
    linkForm.hidden = false;
    linkInput.focus();
  } }, 'link', 'addLink');
  const hideForm = () => {
    if (linkForm.hidden) return;
    const focused = linkForm.contains(document.activeElement);
    linkForm.hidden = true;
    linkInput.value = '';
    // The focus goes back to the tool it came from, never to the page's body.
    if (focused) addLinkButton.focus();
  };
  const linkInput = h('input', { id: 'link-url', type: 'url', required: true, pattern: 'https?://.+', placeholder: 'https://', autocomplete: 'off', spellcheck: 'false', 'aria-label': () => t('linkUrl'),
    onkeydown: (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.stopPropagation();
      hideForm();
    } });
  const linkForm = h('form', { id: 'link-form', hidden: true,
    // Hidden when the focus leaves the form (Tab still reaches its button), on Enter, and on the button.
    onfocusout: (event: FocusEvent) => {
      if (!linkForm.contains(event.relatedTarget as Node | null)) hideForm();
    },
    onsubmit: (event: SubmitEvent) => {
      event.preventDefault();
      const url = httpUrl(linkInput.value);
      if (url) addLink(url, spot());
      hideForm();
    } }, linkInput,
  // Pressing the button keeps the focus in the field, so the form is not hidden before the click.
  iconButton({ type: 'submit', onpointerdown: (event: PointerEvent) => event.preventDefault() }, 'plus', 'addLink'));
  const tools = h('div', { class: 'board-tools' },
    iconButton({ id: 'add-note', onclick: () => change(newId(), { kind: 'note', text: '', ...spot() }) }, 'note', 'addNote'),
    iconButton({ id: 'add-image', onclick: () => imageInput.click() }, 'image', 'addImage'),
    addLinkButton, imageInput, linkForm);

  // Files and an http(s) URL dropped on the board go where they are dropped; pasted ones, at a free spot.
  const dropped = (event: DragEvent) => {
    event.preventDefault();
    delete area.dataset.drop;
    const data = event.dataTransfer;
    if (!data) return;
    const { x, y } = at(event, CARD, CARD);
    // Several files land a step apart, so none hides another.
    if (data.files.length > 0) [...data.files].forEach((file, i) => void share(file, inside(x + i * 0.03, y + i * 0.03, CARD, CARD)));
    else {
      const url = httpUrl(data.getData('URL') || data.getData('text/plain'));
      if (url) addLink(url, { x, y });
    }
  };
  const pasted = (event: ClipboardEvent) => {
    const data = event.clipboardData;
    if (view.hidden || !data || document.activeElement?.matches('input, textarea, select, [contenteditable]')) return;
    const url = data.files.length === 0 ? httpUrl(data.getData('text/plain')) : undefined;
    if (data.files.length === 0 && !url) return;
    event.preventDefault();
    for (const file of data.files) void share(file, spot());
    if (url) addLink(url, spot());
  };
  document.addEventListener('paste', pasted);
  const view = h('div', { id: 'board-view', hidden: true, ondrop: dropped,
    ondragover: (event: DragEvent) => {
      event.preventDefault();
      area.dataset.drop = '';
    },
    ondragleave: () => delete area.dataset.drop }, tools, area);

  return {
    view,
    place: (id, meta) => {
      showBoard();  // first, so the spot is measured on the board as shown
      place(id, meta, spot());
    },
    shareFile: (id, meta) => write(`${FILE}${id}`, { by: me, at: now(), ...meta }),
    removeFile: id => write(`${FILE}${id}`, { deleted: true, at: now() }),
    refresh: render,
    close: () => {
      closed = true;
      document.removeEventListener('paste', pasted);
      others.close();
      mine.close();
    },
  };
}
